import { useEffect, useMemo, useState } from 'react';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import ExportButtons from '../components/ExportButtons';
import LoadingState from '../components/LoadingState';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { getClientSummary, getStateSummary } from '../services/reportService';
import { exportRowsToCsv, exportSheetsToXlsx } from '../utils/exporters';

export default function StateClientSummaryPage() {
  const { activeUpload, loading: uploadLoading } = useActiveUpload();
  const { startDate, endDate } = useDateRange();
  const [stateRows, setStateRows] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeUpload?.id) return;
      setLoading(true);
      setError('');
      try {
        const [states, clients] = await Promise.all([
          getStateSummary(activeUpload.id, startDate, endDate),
          getClientSummary(activeUpload.id, startDate, endDate),
        ]);
        if (!cancelled) {
          setStateRows(states.map((row) => ({ ...row, project_count: Number(row.project_count || 0) })));
          setClientRows(clients.map((row) => ({ ...row, project_count: Number(row.project_count || 0) })));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeUpload?.id, startDate, endDate]);

  const exportRows = useMemo(() => [
    ...stateRows.map((row) => ({ Section: 'Projects per State', Name: row.state, 'Project Count': row.project_count })),
    ...clientRows.map((row) => ({ Section: 'Projects per Client', Name: row.client, 'Project Count': row.project_count })),
  ], [stateRows, clientRows]);

  if (uploadLoading) return <LoadingState message="Loading active upload..." />;
  if (!activeUpload) return <EmptyState title="No active report" message="Ask an admin to upload and activate a Project Milestone Report." />;

  return (
    <div className="page-stack state-client-page">
      <div className="page-title-row">
        <div>
          <h2>State & Client Summary</h2>
          <p>Initial Project Delivery only, filtered by Dashboard date range: {startDate} to {endDate}</p>
        </div>
        <ExportButtons
          onCsv={() => exportRowsToCsv(exportRows, `state-client-summary-${startDate}-to-${endDate}.csv`)}
          onXlsx={() => exportSheetsToXlsx({ States: stateRows, Clients: clientRows }, `state-client-summary-${startDate}-to-${endDate}.xlsx`)}
        />
      </div>
      <ErrorMessage message={error} />
      {loading ? <LoadingState message="Loading summaries..." /> : (
        <>
          <div className="summary-metadata-row">
            <div className="mini-meta-card"><span>Project date source</span><strong>Milestone End Date</strong></div>
            <div className="mini-meta-card"><span>Milestone Type</span><strong>Initial Project Delivery</strong></div>
            <div className="mini-meta-card"><span>States</span><strong>{stateRows.length}</strong></div>
            <div className="mini-meta-card"><span>Clients</span><strong>{clientRows.length}</strong></div>
          </div>
          <div className="grid-two dashboard-chart-row">
            <div className="panel workbook-table-panel">
              <div className="section-bar">Projects per State</div>
              <DataTable
                columns={[
                  { key: 'state', label: 'State' },
                  { key: 'project_count', label: 'Project Count', align: 'right' },
                ]}
                rows={stateRows}
              />
            </div>
            <ChartCard title="Projects per State" data={stateRows} xKey="state" height={420} />
          </div>
          <div className="grid-two dashboard-chart-row">
            <div className="panel workbook-table-panel">
              <div className="section-bar">Projects per Client</div>
              <DataTable
                columns={[
                  { key: 'client', label: 'Client Name' },
                  { key: 'project_count', label: 'Project Count', align: 'right' },
                ]}
                rows={clientRows}
              />
            </div>
            <ChartCard title="Projects per Client" data={clientRows} xKey="client" height={520} />
          </div>
        </>
      )}
    </div>
  );
}
